import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useShop } from '../context/ShopContext';
import { useBrand } from '../context/BrandContext';
import { Coffee } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';

const Onboarding = () => {
  const { brand } = useBrand();
  const { t } = useTranslation();
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    slug: ''
  });
  const [loading, setLoading] = useState(false);
  const { createShop } = useShop();
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSlugChange = (e) => {
    const slug = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '');
    setFormData(prev => ({
      ...prev,
      slug
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const payload = { name: formData.name, description: formData.description, slug: formData.slug };
    const result = await createShop(payload);
    if (result.success) {
      toast.success(t('onboarding.created', 'Coffee shop created successfully!'));
      navigate('/dashboard');
    } else {
      toast.error(result.error);
    }
    setLoading(false);
  };

  const canGoNext = () => {
    if (step === 1) return !!formData.name;
    if (step === 2) return !!formData.slug;
    return false;
  };

  const handleEnterKey = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (step === 1 && formData.name) {
        setStep(2);
      } else if (step === 2 && formData.slug) {
        handleSubmit(e);
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#1f2937] to-[#111827] flex items-center justify-center p-4">
      <div className="bg-[#1f2937]/60 backdrop-blur-sm rounded-2xl p-8 w-full max-w-2xl border border-gray-700/50">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            <Coffee className="text-[#a15507] w-12 h-12" />
          </div>
          <h1 className="text-4xl font-bold text-white mb-2">
            {t('onboarding.welcome', 'Welcome to')} {brand.name}
          </h1>
          <p className="text-gray-300">{t('onboarding.subtitle', "Let's set up your coffee shop")}</p>
          <div className="mt-4 flex justify-center space-x-2 rtl:space-x-reverse">
            {[1, 2].map((stepNumber) => (
              <div
                key={stepNumber}
                className={`w-3 h-3 rounded-full ${
                  step >= stepNumber ? 'bg-[#a15507]' : 'bg-gray-600/50'
                }`}
              />
            ))}
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6" onKeyDown={handleEnterKey}>
          {step === 1 && (
            <div className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-2">{t('onboarding.shopName', 'Coffee Shop Name')}</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="input-field"
                  placeholder={t('onboarding.namePlaceholder', 'e.g., Moka Cafe')}
                />
              </div>

              <div>
                <label htmlFor="description" className="block text-sm font-medium text-gray-300 mb-2">{t('onboarding.description', 'Description')}</label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows="3"
                  className="input-field"
                  placeholder={t('onboarding.descriptionPlaceholder', 'Tell customers about your coffee shop...')}
                />
              </div>

              <button
                type="button"
                onClick={() => setStep(2)}
                disabled={!canGoNext()}
                className="btn-primary w-full"
              >
                {t('onboarding.next', 'Next Step')}
              </button>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-6">
              <div>
                <label htmlFor="slug" className="block text-sm font-medium text-gray-300 mb-2">{t('onboarding.slug', 'Shop URL Slug')}</label>
                <div className="flex items-center rtl:space-x-reverse">
                  <span className="text-gray-300 mr-2 rtl:ml-2 rtl:mr-0">{brand.name.toLowerCase()}.com/</span>
                  <input
                    type="text"
                    id="slug"
                    name="slug"
                    value={formData.slug}
                    onChange={handleSlugChange}
                    required
                    className="input-field flex-1"
                    placeholder={t('onboarding.slugPlaceholder', 'mokacafe')}
                  />
                </div>
                <p className="text-sm text-gray-300 mt-1">{t('onboarding.slugHint', 'This will be your unique URL for customers to access your menu')}</p>
              </div>

              <div className="flex space-x-4">
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className="btn-secondary flex-1"
                >
                  {t('onboarding.previous', 'Previous')}
                </button>
                <button
                  type="submit"
                  disabled={loading || !canGoNext()}
                  className="btn-primary flex-1"
                >
                  {loading ? t('onboarding.creating', 'Creating Shop...') : t('onboarding.create', 'Create My Coffee Shop')}
                </button>
              </div>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default Onboarding; 